import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { MeetingReviewModule } from '@core/components/meeting-review/meeting-review.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import {
  AccountService,
  CommentService,
  FeaturesService,
  FilesService,
  MeetingsService,
  UserServicesBffService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';

import { ComplaintMeetingFormComponent } from '@shared/complaint-meeting-form/complaint-meeting-form.component';
import { IconComponent } from '@shared/icon/icon.component';
import { RatingComponent } from '@shared/rating/rating.component';
import { FeatureEnum, ImageSizeEnum, Meeting, ReviewEntryType, ReviewListItem, User, UserService } from 'lingo2-models';
import { FormatPipeModule } from 'lingo2-ngx-date-fns';
import { ClipboardModule } from 'ngx-clipboard';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiButtonModule, OnUiButtonState, OnUiCover, OnUiCoverModule, OnUiTextareaModule } from 'onclass-ui';
import { first, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-meeting-review-form',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    ClipboardModule,

    FormatPipeModule,
    FormsModule,
    OnUiButtonModule,
    IconComponent,
    OnUiCoverModule,
    OnUiTextareaModule,
    MeetingReviewModule,
    RatingComponent,
    ModalModule,
    ComplaintMeetingFormComponent,
    MobileModalModule,
  ],
  templateUrl: './meeting-review-form.component.html',
  styleUrls: ['./meeting-review-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingReviewFormComponent extends ChangableComponent implements OnInit {
  protected commentService = inject(CommentService);
  protected accountService = inject(AccountService);
  protected meetingService = inject(MeetingsService);
  protected userServicesService = inject(UserServicesBffService);
  protected features = inject(FeaturesService);
  deviceService = inject(DeviceDetectorService);

  @Input()
  public set meetingReview(meetingReview: ReviewListItem) {
    if (meetingReview) {
      this._meetingReview = meetingReview;
      this.prepareCard();
    }
  }

  public get meetingReview(): ReviewListItem {
    return this._meetingReview;
  }

  @Output() loaded = new EventEmitter<boolean>();

  public submitState: OnUiButtonState = 'default';
  public author: User;
  public meeting: Meeting | UserService;
  public meetingCover: OnUiCover;
  public meetingTitle: string;
  public meetingSubject: string;
  public isRated: boolean;
  public toggleComplaintModal: boolean;
  private _meetingReview: ReviewListItem;

  ngOnInit() {
    this.isRated = !!this.meetingReview.rating;
  }

  protected prepareCard() {
    if (this._meetingReview.entryType === ReviewEntryType.meeting) {
      this.meetingService
        .getMeetingById(this._meetingReview?.entryId)
        .pipe(first(), takeUntil(this.destroyed$))
        .subscribe({
          next: (meeting) => {
            this.meeting = meeting;
            this.loadMeetingInfo();
            this.loadAuthor(meeting?.author_id);
            this.loaded.emit(true);
            this.detectChanges();
          },
          error: (error) => {
            this.loaded.emit(false);
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    } else if (this._meetingReview.entryType === ReviewEntryType.userService) {
      this.userServicesService
        .getOne({ id: this._meetingReview.entryId, details: ['author_id', 'author'] })
        .pipe(first(), takeUntil(this.destroyed$))
        .subscribe({
          next: (userservice) => {
            this.meeting = userservice;
            this.loadMeetingInfo();
            this.loadAuthor(userservice?.author_id);
            this.loaded.emit(true);
            this.detectChanges();
          },
          error: (error) => {
            this.loaded.emit(false);
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }
  }

  public loadMeetingInfo() {
    this.meetingSubject = this.meeting.subject ? this.meeting.subject.title : null;

    this.meetingCover = {
      img: FilesService.getFileUrlBySize(this.meeting.cover_id, ImageSizeEnum.wide || ImageSizeEnum.md),
    };
    this.meetingTitle = this.meeting.title;
  }

  public setRating(value: number) {
    this.meetingReview.rating = value;
    this.detectChanges();
  }

  public changeMode() {
    this.isRated = false;
  }

  public get isAvailable() {
    return this.features.isAvailable(FeatureEnum.profile_reviews);
  }

  public openComplaintModal() {
    this.toggleComplaintModal = !this.toggleComplaintModal;
  }

  public submit() {
    if (this.submitState !== 'default') {
      return;
    }

    if (this._meetingReview.rating < 1) {
      return;
    }

    this.submitState = 'progress';
    this.commentService
      .updateReview(this.meetingReview)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (v) => {
          this.isRated = true;
          this.submitState = 'default';
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.submitState = 'fail';
          this.detectChanges();
        },
      });
  }

  protected loadAuthor(author_id) {
    this.accountService
      .getUserById(author_id, 'LOAD_TIME')
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (account) => {
          this.author = account;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public trackByFn(index) {
    return index;
  }
}
